import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { VisuallyHidden } from '@overdose/components'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import {
  CategoryNameLocation,
  ProductList,
  PromoBannerCardSummmary,
  SiteCodes,
} from 'shared-types'
import {
  FeaturedProductCard,
  ProductCard,
  DisruptorPromoCard,
  DisruptorProductCard,
} from '~/components'
import { NoProductsFound } from '~/components/NoProductsFound'
import { ProductListFilters } from '~/components/ProductListFilters/ProductListFilters'
import ProductListRelatedKeywords from '~/components/ProductListRelatedKeywords'
import RichText from '~/components/RichText/RichText'
import { SectionContainer } from '~/components/SectionContainer'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { useAlgoliaSearchInsignts, usePage } from '~/hooks'
import { GTM, GTMEvent } from '~/lib/gtm/GTM'
import {
  FilterProvider,
  useFilterContext,
} from '~/providers/FilterProvider/FilterProvider'
import {
  PaginationProvider,
  PaginationProviderInfo,
  usePaginationContext,
} from '~/providers/PaginationProvider/PaginationProvider'
import {
  ProductListingCoreProps,
  ProductListingProps,
} from '~/sections/ProductListing/ProductListing.types'
import styles from './ProductListing.module.scss'
import { ProductListingCategoryTiles } from './ProductListingCategoryTiles'
import { ProductListingHead } from './ProductListingHead'

const CONST_ONE_COLUMN = 1
const CONST_THREE_COLUMN = 3
const FEW_PRODUCTS_THRESHOLD = 24
const MOBILE_SCROLL_TO_TOP_MIN_PRODUCTS_THRESHOLD = 7
const MOBILE_MAX_WIDTH = 1023

const ProductListing: FC<ProductListingCoreProps> = ({
  products: initialProducts,
  width,
  sort,
  categoryNameAlignment = 'left',
  categoryNameColor,
  paragraph,
  relatedKeywords,
  categoryDetails,
  backgroundImage,
  brazeBannerCards,
  queryId,
  slug,
  limit,
  isSearchPage,
  isCategoryPage,
  hideCategoryName = false,
  categoryNameLocation = CategoryNameLocation.InsideBanner,
  resolvedCustomerType,
}) => {
  const { siteCode, pageWidth } = usePage()
  const {
    searchType,
    searchQuery,
    searchFilters,
    selectedFilterMap,
    selectedSortOption,
  } = useFilterContext()
  const {
    category,
    pagination,
    setPagination,
    renderPreviousPagination,
    renderNextPagination,
    setCategorySlug,
    setLimit,
  } = usePaginationContext()

  if (slug) {
    setCategorySlug(slug)
    setLimit(limit)
  }

  const paginationButtonProps = {
    className: classNames(styles.loadMoreButton, {
      [styles.rtm]: siteCode === SiteCodes.RTM,
    }),
  }

  const { updateLatestQueryId, sendViewProductsEvent, sendFilterEvent } =
    useAlgoliaSearchInsignts()

  const [products, setProducts] = useState<ProductList>(initialProducts)

  const [prevProducts, setPrevProducts] = useState(initialProducts)
  const router = useRouter()

  const pageNumber = Number(router?.query?.p) || null
  const [initialPage, setInitialPage] = useState(pageNumber)
  const longDescriptionRef = useRef(null)

  const scrollToDescription = () => {
    if (longDescriptionRef.current) {
      longDescriptionRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  if (pageNumber === 1 && initialPage) {
    setInitialPage(null)
  }

  if (initialProducts !== prevProducts) {
    setPrevProducts(initialProducts)
    setProducts(initialProducts)
    setPagination((prevPagination) => {
      return {
        ...prevPagination,
        currentPage: pageNumber || 1,
      }
    })
  }

  useEffect(() => {
    updateLatestQueryId(queryId)
  }, [queryId, updateLatestQueryId])

  useEffect(() => {
    sendFilterEvent(selectedFilterMap, 'view')
  }, [selectedFilterMap, sendFilterEvent])

  useEffect(() => {
    if (category?.name) {
      sendFilterEvent({ 'categories.name': [category?.name] }, 'view')
    }
  }, [category?.name, sendFilterEvent])

  useEffect(() => {
    if (!searchQuery) {
      return
    }
    GTM.dispatch(GTMEvent.CLEAR_ECOMMERCE)
    GTM.dispatch(GTMEvent.VIEW_SEARCH_RESULTS, {
      products,
      searchTerm: searchQuery?.selectedOptions?.[0],
      searchResultCount: pagination.totalProducts ?? 0,
      searchFilters: searchFilters || 'NA',
      searchType,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilters])

  const dispatchViewItemListWasCalled = useRef(false)
  const algoliaProductViewEventWasCalled = useRef(false)

  useEffect(() => {
    if (!products || dispatchViewItemListWasCalled.current) {
      return
    }
    dispatchViewItemListWasCalled.current = true

    GTM.dispatch(GTMEvent.CLEAR_ECOMMERCE)
    GTM.dispatch(GTMEvent.VIEW_ITEM_LIST, {
      products,
      category,
    })
  }, [category, products])

  useEffect(() => {
    if (!products || algoliaProductViewEventWasCalled.current) {
      return
    }
    algoliaProductViewEventWasCalled.current = true
    sendViewProductsEvent(products)
  }, [products, sendViewProductsEvent])

  const productsAvailable = !!products?.length

  const renderCategoryDetails = categoryDetails && (
    <div
      ref={longDescriptionRef}
      className='flex flex-col items-start gap-4 mb-20'>
      <Typography
        tag={TypographyTag.p}
        className='flex justify-center'
        variant={TypographyVariant.BodyXLargeBold}>
        {categoryDetails.title}
      </Typography>
      <RichText content={categoryDetails.description.json} />
    </div>
  )

  const renderRelatedKeywords = relatedKeywords && (
    <div className='mb-16 lg:mb-20'>
      <ProductListRelatedKeywords {...relatedKeywords} />
    </div>
  )

  const showCategoriesAndRelatedWords = !!(
    (relatedKeywords && Object?.keys(relatedKeywords).length) ||
    (categoryDetails && Object?.keys(categoryDetails).length)
  )

  const areCategoriesAvailable = category?.children?.length > 0

  const renderNextPageControls = () => {
    const hasTooFewProducts = pagination?.totalProducts < FEW_PRODUCTS_THRESHOLD
    const showScrollToTopForMobileView =
      pageWidth <= MOBILE_MAX_WIDTH &&
      pagination?.totalProducts >= MOBILE_SCROLL_TO_TOP_MIN_PRODUCTS_THRESHOLD

    return renderNextPagination({
      productCnt: products?.length,
      filterInfo: {
        searchType,
        searchQuery,
        searchFilters,
        selectedSortOption,
        selectedFilterMap,
        setProducts,
      },
      action: paginationButtonProps,
      showScrollToTop: !hasTooFewProducts || showScrollToTopForMobileView,
      showOnLastPage: true,
      showProgressBar: !hasTooFewProducts,
    })
  }

  return (
    <SectionContainer
      width={width}
      wrapperClassName={classNames('productListingSection', {
        'mb-8 lg:mb-20': !showCategoriesAndRelatedWords,
      })}>
      <ProductListingHead
        backgroundImage={backgroundImage}
        brazeBannerCards={brazeBannerCards}
        hasSearchQuery={!!searchQuery}
        categoryName={category?.name}
        categoryNameAlignment={categoryNameAlignment}
        categoryNameColor={categoryNameColor}
        hideCategoryName={hideCategoryName}
        categoryNameLocation={categoryNameLocation}
        paragraph={paragraph}
        onViewMore={scrollToDescription}
      />
      <ProductListingCategoryTiles category={category} />
      {!!searchQuery && (
        <div className='flex flex-wrap flex-col md:flex-row gap-3.5 md:items-baseline mb-8 mt-2'>
          <Typography
            variant={TypographyVariant.Heading3}
            tag={TypographyTag.h1}
            className={classNames(
              styles.searchTitle,
              'flex flex-row flex-wrap 2xl:items-baseline gap-2',
              {
                'flex justify-center': categoryNameAlignment === 'center',
              }
            )}>
            {!category?.url && (
              <span className='font-normal'>Showing results for </span>
            )}
            <span>{category?.name || ''}</span>
          </Typography>
          <Typography
            variant={TypographyVariant.BodyRegular}
            tag={TypographyTag.div}
            className='text-brand-primary'>
            ({pagination?.totalProducts || 0} Products Found)
          </Typography>
        </div>
      )}
      {productsAvailable && (
        <div
          className={classNames({
            'mt-6 md:mt-8': !areCategoriesAvailable,
            'mt-2 md:mt-4': areCategoriesAvailable,
          })}>
          <ProductListFilters
            sort={sort}
            isSearchPage={isSearchPage}
            isCategoryPage={isCategoryPage}
            siteCode={siteCode}
          />
        </div>
      )}
      {initialPage > 1 && (
        <div className='flex justify-center mt-6'>
          {renderPreviousPagination({
            action: paginationButtonProps,
          })}
        </div>
      )}
      <Typography variant={TypographyVariant.Heading2} tag={TypographyTag.h2}>
        <VisuallyHidden>Featured Products</VisuallyHidden>
      </Typography>
      <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 3xl:grid-cols-6 gap-x-4 gap-y-4 md:gap-y-10 pt-6 pb-10'>
        {productsAvailable ? (
          products.map((product: PromoBannerCardSummmary, index) => {
            let imagePriority = false
            if (index < Number('6')) {
              imagePriority = true
            }

            if (product?.isFeatured) {
              return (
                <div
                  key={index}
                  className={classNames({
                    'col-span-2 lg:col-span-3':
                      product.columnSpan === CONST_THREE_COLUMN,
                    'col-span-1': product?.columnSpan === CONST_ONE_COLUMN,
                  })}>
                  <FeaturedProductCard
                    {...product}
                    tags={product?.tags}
                    showAddToCartButton={product?.showAddToCartButton}
                    finalPrice={product?.finalPrice}
                    regularPrice={product?.regularPrice}
                    image={product?.thumbnail}
                    url={product?.url}
                    imagePriority={imagePriority}
                    category={category}
                    availableForDelivery={product?.availableForDelivery}
                    inStockText={product?.inStockText}
                  />
                </div>
              )
            }
            if (product?.isPromoBanner) {
              return <DisruptorPromoCard key={index} {...product} />
            }

            if (product?.isProductDisruptor) {
              return (
                <div key={index} className='col-span-2 lg:col-span-3'>
                  <DisruptorProductCard {...product} />
                </div>
              )
            }

            return (
              <div key={index} className='col-span-1'>
                <ProductCard
                  title={product?.title}
                  url={product?.url}
                  {...product}
                  sku={product?.sku}
                  finalPrice={product?.finalPrice}
                  regularPrice={product?.regularPrice}
                  resolvedCustomerType={resolvedCustomerType}
                  showAddToCartButton
                  thumbnail={product?.thumbnail}
                  index={index}
                  category={category}
                  imagePriority={imagePriority}
                  availableForDelivery={product?.availableForDelivery}
                  inStockText={product?.inStockText}
                  tags={product?.tags}
                  titleSection={category?.name || 'category'}
                  searchIndex={index + 1}
                  queryId={queryId}
                  showCompactAddToCart={false}
                />
              </div>
            )
          })
        ) : (
          <div className='flex justify-start'>
            <NoProductsFound />
          </div>
        )}
      </div>
      <div className='flex justify-center'>{renderNextPageControls()}</div>
      {showCategoriesAndRelatedWords && (
        <>
          <hr className='border-t my-20' />
          {renderCategoryDetails}
          {renderRelatedKeywords}
        </>
      )}
    </SectionContainer>
  )
}

const ProductListingWithPagination: FC<
  ProductListingCoreProps & PaginationProviderInfo
> = (props) => {
  const { category, totalPages, currentPage, totalProducts, ..._props } = props

  const values = useMemo(() => {
    return {
      category,
      totalPages,
      currentPage,
      totalProducts,
    }
  }, [category, currentPage, totalPages, totalProducts])

  return (
    <PaginationProvider {...values}>
      <ProductListing {..._props} />
    </PaginationProvider>
  )
}

export const ProductListingWrapper: FC<ProductListingProps> = (props) => {
  const { selectedFilters, filters, searchType, selectedSort, ..._props } =
    props

  const { asPath } = useRouter()
  const [basePath, params] = asPath.split('?')
  const filteredParams = params?.split('&').find((param) => {
    return param.includes('q=')
  })

  // Key ensures the PLP filters reset when search query changes
  const key = `${basePath}${filteredParams || ''}`

  return (
    <FilterProvider
      filters={filters}
      searchType={searchType}
      selectedFilters={selectedFilters}
      selectedSort={selectedSort}
      key={key}>
      <ProductListingWithPagination {..._props} />
    </FilterProvider>
  )
}

export default ProductListingWrapper
